import he from "he";

/** Strip HTML to plain text (e.g. for AI outreach context). */
export function stripHtmlToText(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body?.textContent || doc.body?.innerText || "").replace(/\s+/g, " ").trim();
}

/**
 * Format plain text for display: decode entities, linkify URLs, newlines → <br>, wrap in <p>.
 * Used for Reddit plain text and as fallback when content isn't HTML.
 */
function formatPlainTextBody(body) {
  if (!body) return "";
  const decoded = he.decode(body);
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const urlPlaceholder = "___LINK_";
  const urls = [];
  const withPlaceholders = decoded.replace(urlRegex, (url) => {
    urls.push(url);
    return urlPlaceholder + (urls.length - 1) + "___";
  });
  const escaped = withPlaceholders
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const linkRe = new RegExp(urlPlaceholder + "(\\d+)" + "___", "g");
  const withLinks = escaped.replace(linkRe, (_, i) => {
    const url = urls[Number(i)];
    const safeHref = url.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    const safeText = url.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="post-modal-body-link">${safeText}</a>`;
  });
  const withBreaks = withLinks.replace(/\n/g, "<br />");
  const paragraphs = withBreaks.split(/(<br \/>)\1+/).filter(Boolean);
  return paragraphs
    .map((block) => {
      const trimmed = block.replace(/^(<br \/>)+|(<br \/>)+$/g, "").trim();
      if (!trimmed) return "";
      return `<p class="post-modal-body-p">${trimmed}</p>`;
    })
    .join("");
}

/**
 * Sanitize Reddit post body for safe display.
 * - If content looks like HTML (contains tags), parse and return body content only; links get target/_blank.
 * - Otherwise treat as plain text: decode, linkify URLs, preserve newlines/paragraphs.
 */
export function sanitizeBody(body) {
  if (!body) return "";
  const trimmed = body.trim();
  if (!trimmed) return "";
  const decodedBody = he.decode(trimmed);
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(decodedBody);
  if (looksLikeHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(decodedBody, "text/html");
    doc.querySelectorAll("a").forEach((linkElement) => {
      linkElement.setAttribute("target", "_blank");
      linkElement.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body ? doc.body.innerHTML : formatPlainTextBody(trimmed);
  }
  return formatPlainTextBody(trimmed);
}

/** Format LinkedIn post body (plain text): preserve newlines, linkify URLs, escape HTML for safe display. */
export function formatLinkedInBody(body) {
  return formatPlainTextBody(body);
}
